import React, { useEffect, useRef, useState } from "react"

import { Portal } from "@radix-ui/react-portal"

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

const clampStyle = (lines = 2) =>
  ({
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
  }) as React.CSSProperties

interface ClampedTextProps {
  text: string
  lines?: number
  className?: string
  disableTooltip?: boolean
}

export const ClampedText: React.FC<ClampedTextProps> = ({
  text,
  lines = 2,
  className = "",
  disableTooltip = false,
}) => {
  const spanRef = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const el = spanRef.current
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1)
    }
  }, [text, lines])

  if (!text) return <span>-</span>

  const content = (
    <span
      ref={spanRef}
      style={clampStyle(lines)}
      className={`block text-sm leading-snug ${className} cursor-default`}
    >
      {text}
    </span>
  )

  if (disableTooltip || !isOverflowing) return content

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <Portal>
          <TooltipContent
            side="top"
            align="center"
            onClick={(e) => e.stopPropagation()}
            className="z-[9999] max-w-lg break-words rounded-lg px-4 py-2 text-sm shadow-xl"
          >
            {text}
          </TooltipContent>
        </Portal>
      </Tooltip>
    </TooltipProvider>
  )
}
